import {
	Button,
	Input,
	Modal,
	ModalBody,
	ModalContent,
	Switch,
	Tooltip,
	useDisclosure
} from "@heroui/react";
import {twMerge} from 'tailwind-merge';
import {useRef, useState} from 'react';
import {useForm} from '@mantine/form';
import _ from 'lodash';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import {Dropzone, DropzoneAccept, DropzoneIdle, DropzoneReject} from '@mantine/dropzone';
import PageLoader from '../components/PageLoader.jsx';
import {useRecoilState} from 'recoil';
import {userState} from '../store.jsx';

const inputWrapper = 'min-h-12 bg-default-500/5 border-1 data-[focus=true]:!border-primary data-[hover=true]:border-primary shadow-none';
const formData = new FormData();
formData.set('file', null)

export default function VoiceModelGenerate({
	                                           title = "",
	                                           children,
	                                           data = null,
	                                           isClone = false,
	                                           triggerContent,
	                                           triggerProps,
	                                           ...props
                                           }) {
	const {isOpen, onOpen, onOpenChange} = useDisclosure();
	const [fileName, setFileName] = useState('');
	const [authMe] = useRecoilState(userState);
	const form = useForm({
		initialValues: {
			title: 'Voice 1',
			remove_background_noise: false,
		}
	})
	const queryClient = useQueryClient()
	
	const openRef = useRef();
	
	const onSaveGeneratedVoice = useMutation({
		mutationFn: repositories.project.generateVoice,
		onSuccess: async () => {
			toast.success('Save generated voice')
			onOpenChange();
			await queryClient.invalidateQueries(['voiceList'])
		},
		onError: (error) => {
			toast.error(_.replace(error?.response?.data?.detail, 'body.', '') || 'Oops, something went wrong')
		}
	})
	
	return (
		<>
			<Tooltip
				showArrow
				placement="top-end"
				classNames={{
					content: 'py-3 px-6 rounded-2xl'
				}}
				content={
					<div className="flex items-center gap-3">
						<i className="hgi-stroke hgi-voice-id text-xl"></i>
						<span>Add new voice</span>
					</div>}
			>
				<Button
					isDisabled
					//isDisabled={!(_.get(authMe, 'current_plan', 0) > 1)}
					onPress={onOpen} variant="solid" radius="lg" {...triggerProps} className={twMerge([
					"capitalize h-full max-h-fit min-h-12 py-2.5 min-w-40 bg-black text-white font-sans",
					triggerProps?.className
				])}>
					<i className="hgi-stroke hgi-plus-sign text-xl"></i>
					Coming soon {/* Clone voice*/}
				</Button>
			</Tooltip>
			
			
			<Modal
				classNames={{
					base: 'overflow-hidden rounded-3xl border border-[#12131714] bg-white shadow-2xl',
					body: 'p-0',
					backdrop: 'bg-gradient-to-t from-zinc-900 to-zinc-900/10 backdrop-opacity-20 backdrop-blur-sm',
				}}
				size="2xl"
				hideCloseButton
				isOpen={isOpen}
				onOpenChange={onOpenChange}>
				<ModalContent>
					{(onClose) => (
						<ModalBody>
							{/* Hero band — primary */}
							<div className="relative overflow-hidden bg-gradient-to-br from-[#7659fa] via-[#8a6cff] to-[#a78bfa] px-8 pt-8 pb-9">
								<div className="pointer-events-none absolute -top-10 -right-8 h-32 w-32 rounded-full bg-white/15 blur-2xl" />
								<div className="pointer-events-none absolute -bottom-12 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
								<div className="relative flex items-center gap-4">
									<div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/40">
										<i className="hgi-stroke hgi-voice-id text-2xl text-white"></i>
									</div>
									<div>
										<h2 className="text-xl font-semibold text-white">Create a clone voice</h2>
										<p className="mt-0.5 text-sm font-light text-white/80">Upload a clean audio sample to clone a voice.</p>
									</div>
								</div>
							</div>

							{/* Form */}
							<div className="grid grid-cols-1 gap-5 px-6 py-6">
								<div>
									<label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">Voice name</label>
									<Input
										placeholder="Enter voice name"
										isRequired
										type="text"
										startContent={<i className="hgi-stroke hgi-voice-id text-sm text-slate-400" />}
										{...form.getInputProps('title')}
										classNames={{
											inputWrapper: 'h-[44px] bg-[#fcfcfd] rounded-xl border border-[#12131714] focus-within:!border-[#7659fa]',
											input: '!text-sm text-slate-700 placeholder:text-gray-400',
										}}
									/>
								</div>

								<div
									className="text-center grow h-[initial] border-dashed border-2 py-10 overflow-hidden border-[#7659fa33] bg-[#fcfcfd] rounded-2xl cursor-pointer flex flex-col items-center justify-center transition hover:border-[#7659fa66]">
									<Dropzone
										classNames={{
											root: 'w-full h-full',
											inner: 'w-full h-full flex items-center'
										}}
										accept={["audio/*"]}
										onDrop={([file]) => {
											formData.append('file', file)
											setFileName(file?.name)
										}}>
										<DropzoneAccept>
											<div className="w-full bg-green-50 h-full rounded-2xl">
												<PageLoader/>
											</div>
										</DropzoneAccept>
										<DropzoneReject>
											<div className="w-full">
												<i className="hgi-stroke hgi-mic-off-02 text-red-500 text-6xl max-w-24 mx-auto mb-5"></i>

												<h2 className="font-sans font-bold text-lg">
													This file type is not supported, Please upload a valid file.
												</h2>
												<p className="text-sm text-slate-500 mt-2 mb-5">MP3, ACC, WAV, AIFF</p>
											</div>
										</DropzoneReject>
										<DropzoneIdle>
											{_.isEmpty(fileName) ?
												<div className="w-full">
													<div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-[#7659fa1a] text-[#7659fa]">
														<i className="hgi-stroke hgi-inbox-upload text-2xl"></i>
													</div>
													<h2 className="font-sans font-bold text-lg text-slate-700">Upload file, drag or drop here</h2>
													<p className="text-xs text-slate-400 mt-1">MP3, ACC, WAV, AIFF</p>
												</div>
												:
												<div className="w-full">
													<div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-[#7659fa1a] text-[#7659fa]">
														<i className="hgi-stroke hgi-audio-book-02 text-2xl"></i>
													</div>
													<h2 className="font-sans font-bold text-lg text-slate-700">Uploaded file</h2>
													<p className="text-sm text-slate-500 mt-1 break-all px-6">{fileName}</p>
												</div>
											}
										</DropzoneIdle>
									</Dropzone>
								</div>

								<div className="flex items-center justify-between rounded-2xl border border-[#12131714] bg-[#fcfcfd] px-4 py-3">
									<div className="flex items-center gap-3">
										<i className="hgi-stroke hgi-mic-off-02 text-lg text-slate-400"></i>
										<span className="text-sm font-medium text-slate-700">Remove background noise</span>
									</div>
									<Switch {...form.getInputProps('remove_background_noise')} />
								</div>
							</div>

							{/* Actions */}
							<div className="flex justify-end gap-3 px-6 pb-6">
								<Button
									onPress={onClose}
									variant="bordered"
									className="h-11 rounded-xl border-1 border-[#00000014] bg-[#c8ceda4d] px-6 text-sm font-medium text-slate-700 hover:border-slate-700">
									Cancel
								</Button>
								<Button
									isLoading={onSaveGeneratedVoice.isPending}
									isDisabled={_.isEmpty(fileName)}
									onPress={() => {
										formData.set('name', form.values?.title)
										formData.set('remove_background_noice', form.values?.remove_background_noise)
										setTimeout(() => onSaveGeneratedVoice.mutate(formData), 200)
									}}
									className={`h-11 rounded-xl px-6 text-sm font-medium text-white transition ${_.isEmpty(fileName) ? 'bg-[#b5a7f2] cursor-not-allowed' : 'bg-[#7659fa] shadow-lg shadow-[#7659fa40] hover:opacity-95'}`}>
									<i className="hgi-stroke hgi-tick-02 text-[15px]"/> Generate
								</Button>
							</div>
						</ModalBody>
					)}
				</ModalContent>
			</Modal>
		</>
	)
};
